import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader, XCircle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed, expired
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const registrationId = searchParams.get('registration_id');
  const isResource = searchParams.get('type') === 'resource';
  const backPath = isResource ? '/ressources' : '/catalogue';
  const MAX_ATTEMPTS = 10;
  const POLL_INTERVAL = 2000;

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    if (attempts >= MAX_ATTEMPTS) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`);
      const data = response.data;
      
      setPaymentInfo(data);
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setPollAttempts(attempts + 1);
      setTimeout(() => pollPaymentStatus(attempts + 1), POLL_INTERVAL);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts >= MAX_ATTEMPTS - 1) {
        setStatus('failed');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), POLL_INTERVAL);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-12 text-center">
        {status === 'checking' && (
          <div data-testid="payment-checking">
            <Loader className="mx-auto text-violet-500 animate-spin mb-6" size={80} />
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              Vérification du paiement...
            </h1>
            <p className="text-gray-600 mb-4">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
            <p className="text-sm text-gray-500">
              Tentative {pollAttempts + 1} / {MAX_ATTEMPTS}
            </p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-success">
            <div className="mx-auto mb-6 w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="text-green-500" size={64} />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              Paiement réussi ! 🎉
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              {isResource ? 'Votre ressource est disponible dans votre espace apprenant.' : 'Votre inscription a été confirmée avec succès.'}
            </p>
            
            {paymentInfo && (
              <div className="bg-violet-50 rounded-xl p-6 mb-8 border border-violet-200">
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div>
                    <p className="text-sm text-gray-500">Montant payé</p>
                    <p className="text-2xl font-bold text-violet-600">
                      {paymentInfo.amount?.toFixed(2)} €
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Statut</p>
                    <p className="text-lg font-semibold text-green-600">Confirmé</p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-gray-600 mb-8">
              {isResource
                ? 'Un email vous a été envoyé. Connectez-vous à « Mon espace » avec votre adresse email pour télécharger la ressource.'
                : 'Un email de confirmation vous a été envoyé avec tous les détails de votre inscription.'}
            </p>

            <div className="flex gap-4 justify-center">
              {isResource && (
                <button onClick={() => navigate('/mon-espace')} className="btn-violet" data-testid="go-to-student-space-button">
                  Accéder à mon espace
                </button>
              )}
              <button
                onClick={() => navigate(backPath)}
                className={isResource ? 'px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50' : 'btn-violet'}
                data-testid="back-to-catalog-button"
              >
                {isResource ? 'Retour aux ressources' : 'Retour au catalogue'}
              </button>
            </div>
          </div>
        )}

        {(status === 'failed' || status === 'timeout' || status === 'expired') && (
          <div data-testid="payment-failed">
            <div className="mx-auto mb-6 w-24 h-24 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="text-red-500" size={64} />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              {status === 'expired' ? 'Session expirée' : 'Paiement échoué'}
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              {status === 'timeout' 
                ? "Nous n'avons pas pu confirmer votre paiement à temps. Veuillez vérifier votre email ou réessayer."
                : status === 'expired'
                ? "Votre session de paiement a expiré. Veuillez recommencer."
                : "Une erreur s'est produite lors du paiement. Veuillez réessayer."}
            </p>

            <button
              onClick={() => navigate(backPath)}
              className="btn-violet flex items-center gap-2 mx-auto"
              data-testid="retry-button"
            >
              <ArrowLeft size={20} />
              {isResource ? 'Retour aux ressources' : 'Retour au catalogue'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
